import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { setCurrency } from "../currencySlice";
import { baseApi } from "../../../../app/api";
import Icon from "../../../../common/components/Icon/Icon";

interface CurrencySwitcherProps {
  className?: string;
  variant?: "dropdown" | "inline";
}

const CurrencySwitcher: React.FC<CurrencySwitcherProps> = ({
  className,
  variant = "dropdown",
}) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Read state from the Redux slice
  const { currentCurrency, supportedCurrencies, symbols } = useAppSelector(
    (state) => state.currency,
  );

  // Close dropdown when clicking outside (Capture Phase & Touch Support)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // stopPropagation() engeline takılmamak için 'true' (useCapture) parametresi eklendi
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen]);

  // If there's only one currency (or none), hide the switcher completely
  if (supportedCurrencies.length < 2) {
    return null;
  }

  const handleCurrencySwitch = (currencyCode: string) => {
    setIsOpen(false);
    if (currencyCode === currentCurrency) return;

    dispatch(setCurrency(currencyCode));
    dispatch(baseApi.util.invalidateTags(["Product", "Cart", "Checkout"]));
  };

  const currentSymbol = symbols[currentCurrency] || currentCurrency;

  // --- 1. YAN YANA YAZILI BUTON MODU (Geriye Uyumlu / Legacy) ---
  if (variant === "inline") {
    return (
      <div
        className={`currency-switcher currency-switcher--inline ${className || ""}`}
      >
        <div className="currency-switcher__container">
          {supportedCurrencies.map((currency) => (
            <button
              key={currency}
              onClick={() => handleCurrencySwitch(currency)}
              className={`currency-switcher__option ${
                currency === currentCurrency ? "is-active" : ""
              }`}
              aria-current={currency === currentCurrency ? "true" : "false"}
              aria-label={`Switch to ${currency}`}
            >
              {symbols[currency] || currency}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- 2. DROPDOWN (AÇILIR MENÜ) MODU (Yeni Varsayılan) ---
  return (
    <div
      ref={dropdownRef}
      className={`currency-switcher currency-switcher--dropdown ${className || ""} ${
        isOpen ? "is-open" : ""
      }`}
    >
      <button
        type="button"
        className="currency-switcher__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="currency-switcher__selected-symbol">
          {currentSymbol}
        </span>
        <span className="currency-switcher__selected-name">
          {currentCurrency}
        </span>
        <Icon iconName="chevron-down" className="currency-switcher__chevron" />
      </button>

      {isOpen && (
        <ul className="currency-switcher__menu" role="listbox">
          {supportedCurrencies.map((currency) => {
            const isActive = currency === currentCurrency;
            const symbol = symbols[currency] || currency;

            return (
              <li key={currency} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleCurrencySwitch(currency)}
                  className={`currency-switcher__menu-item ${isActive ? "is-active" : ""}`}
                >
                  <span className="currency-switcher__menu-item-symbol">
                    {symbol}
                  </span>
                  <span>{currency}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CurrencySwitcher;
